import React, { useCallback } from 'react';

import { GridCellWithAvatar } from '@vyce/core/src/components';
import { getTime } from '@vyce/core/src/utils/dates';
import { getAvatar } from '@vyce/core/src/utils';
import { WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { EnrichedColumnProps } from '@vyce/core/src/components/InfiniteTable/types';

import { WidgetTemplate } from './WidgetTemplate';
import { Props } from './types';
import { CheckInWorkerDTO } from '../../../api/types';
import useChatRequests from '../../../hooks/useChatRequests';
import { TimeLogWarningCell } from '../../../components/EditWorkerHoursDialog/components/TimeLogWarningCell';
import { useSchedule } from '../../../hooks/useSchedule';

export const ClockInWidget = (props: Props & WidgetWrapperProps) => {
  const { createPrivateChat } = useChatRequests();
  const { isClockInLate, getShiftSchedule } = useSchedule();

  const goToChat = async (worker: CheckInWorkerDTO) => {
    if (!props.chatUser || !props.selectedCompanyId) {
      return;
    }
    await createPrivateChat({
      creator: props.chatUser,
      chatUser: {
        uid: worker.user_id,
        name: `${worker.first_name} ${worker.last_name}`,
        email: worker.email,
        phone: worker.phone,
        avatarUrl: worker.photo_url,
      },
      companyId: props.selectedCompanyId,
    });
  };

  const getColumns = useCallback(
    (cnaViewWorker: boolean): EnrichedColumnProps<CheckInWorkerDTO>[] => [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 1,
        sortable: true,
        renderCell: row => (
          <GridCellWithAvatar
            avatarUrl={row.photo_url}
            avatarPlaceholder={getAvatar(row.gender)}
            link={cnaViewWorker ? `/time/users/${row.first_name}_${row.user_id}/logs` : ''}
            name={`${row.first_name} ${row.last_name}`}
            target="_blank"
            createPrivateChat={() => goToChat(row)}
          />
        ),
      },
      {
        field: 'job_title',
        headerName: 'Job Title',
        flex: 0.8,
        width: 100,
        sortable: true,
      },
      {
        field: 'site_name',
        headerName: 'Location',
        flex: 0.8,
        width: 100,
        sortable: true,
      },
      {
        field: 'checked_in',
        headerName: 'Since',
        flex: 0.8,
        width: 100,
        sortable: true,
        renderCell: row => (
          <TimeLogWarningCell
            check_in_ok={true}
            face_in_ok={true}
            minutesLate={isClockInLate(
              getShiftSchedule(row.shift_id, props.locations) || [],
              new Date(row.checked_in)
            )}>
            <>{getTime(row.checked_in)}</>
          </TimeLogWarningCell>
        ),
      },
    ],
    []
  );

  return <WidgetTemplate getColumns={getColumns} {...props} type="clock_in" />;
};
